/**
 * Двоичное число в десятичное
 * ====================================
 */
import { Link, LoaderFunctionArgs, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import H2 from "~/components/H2";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import { makeTitle } from "~/helpers/pageTitle";
import { createArticleSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import ToolForm from "~/components/tool-page/ToolForm";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import NumeralSystemsConverter, { NumeralSystem } from "~/components/NumeralSystemsConverter";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import Author from "~/components/tool-page/Author";

const CATEGORY = "numbers";
const TKEY = "calculators.numbers.binary_to_decimal";
const ROUTE_ENDING = "binary-to-decimal";
const LAST_UPDATED = new Date("2024-12-07T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: makeTitle(data?.meta.title) },
  { name: "description", content: data?.meta.description },
  createArticleSchema(
    data?.meta.locale as string,
    data?.meta.title as string,
    [],
    LAST_UPDATED,
    schemaOrg.authorAlexFedorov
  )
];

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "pt", "tr", "it", "pl", "ar", "th"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const number: string | null = params.number ?? null;

  const t = await getTranslator(request);

  const locale = getLocale(request.url);

  // Calculate result if number is set
  let error: string | null = null;
  let decimalNumber: number | null = null;

  try {
    if (number !== null) {
      const isValidBinaryString = /^[01]+$/.test(number);

      if (!isValidBinaryString) {
        error = t(`${TKEY}.error_invalid_number`);
      } else {
        decimalNumber = parseInt(number, 2);
      }
    }
  } catch (e) {
    error = t("error_unknown");
  }

  const title = number
    ? t(`${TKEY}.title_for_number`, { number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  return {
    number,
    decimalNumber,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, decimalNumber })
        : t(`${TKEY}.description`),
      breadcrumbs
    }
  };
};

export default function Index() {
  const {
    meta,
    number,
    decimalNumber,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const commonBinaryNumbers = [
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    50,
    100,
    500,
    1000,
    5000,
    10000,
    100000,
    1000000
  ];

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name={meta.title}
          description={meta.description}
        />

        {decimalNumber && !error ? <Result color="positive">
          <H2 className="first-letter:capitalize">{t("answer")}:</H2>

          <p className="mt-8 text-3xl font-bold first-letter:capitalize font-serif">
            {decimalNumber}
          </p>
        </Result> : null}

        <ToolForm>
          <NumeralSystemsConverter
            from={NumeralSystem.binary}
            to={NumeralSystem.decimal}
            value={number ?? "11"}
          />
        </ToolForm>

        {error ?
          <Result color="negative">
            <p>{error}</p>
          </Result>
          : null}
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_binary_system`)}</H2>
            <p>{t(`${TKEY}.info_p_about_binary_system`)}</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
            <div dangerouslySetInnerHTML={
              { __html: t(`${TKEY}.info_p_how_to_convert`, { interpolation: { escapeValue: false } }) }
            } />
          </AdditionalInfoSection>

          {number !== null ? null : (
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
              <div className="columns-1 md:columns-3 gap-12">
                <table className="table-auto">
                  <tbody>
                  {commonBinaryNumbers.map((number) => (
                    <tr key={number} className="gap-2">
                      <td className="pr-3 text-right"><Link
                        to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/${number.toString(2)}`}>{number.toString(2)}</Link>
                      </td>
                      <td>{number}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </AdditionalInfoSection>
          )}
        </AdditionalInfo>


        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>

    </>
  );
}


